/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/*!
 *!   @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
 *!   @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
 *!   @@@                                         @@@@
 *!   @@@   THIS FILE WAS AUTOGENERATED BY TOOL   @@@@
 *!   @@@   CHANGES MUST TARGET SCRIPTS/SWAGGER   @@@@
 *!   @@@                                         @@@@
 *!   @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
 *!   @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
 *!
 */

import {
  _buildQueryString,
  _emptyParams,
  _encode,
  _getBasePath,
  _getRawBasePath,
} from './urls-utils'

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/security/deployment
 * Operation: `get-security-deployment`
 * Retrieves the current security deployment.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The security deployment was successfully returned. See {@link SecurityDeployment}
 * @see 404 The realm specified by {realm_id} cannot be found. (code: `security_deployment.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSecurityDeploymentUrl(params: GetSecurityDeploymentUrlParams): string {
  const path = _encode`/${params.regionId}/platform/configuration/security/deployment`
  return `${_getBasePath()}${path}`
}

export interface GetSecurityDeploymentUrlParams {
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/configuration/security/deployment
 * Operation: `create-security-deployment`
 * Creates a new security deployment configuration.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link SecurityDeploymentCreateRequest}
 * @see 201 The security deployment was successfully created. See {@link IdResponse}
 * @see 409 * There is a version conflict. (code: `security_deployment.version_conflict`)
 * There is a version conflict. (code: `security_deployment.already_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createSecurityDeploymentUrl(params: CreateSecurityDeploymentUrlParams): string {
  const path = _encode`/${params.regionId}/platform/configuration/security/deployment`
  return `${_getBasePath()}${path}`
}

export interface CreateSecurityDeploymentUrlParams {
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/configuration/security/deployment
 * Operation: `update-security-deployment`
 * Updates an existing security deployment configuration.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link SecurityDeploymentUpdateRequest}
 * @see 200 The security deployment was successfully updated. See {@link IdResponse}
 * @see 404 The security deployment was not found. (code: `security_deployment.not_found`). See {@link BasicFailedReply}
 * @see 409 * There is a version conflict. (code: `security_deployment.version_conflict`)
 * There is a version conflict. (code: `security_deployment.already_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateSecurityDeploymentUrl(params: UpdateSecurityDeploymentUrlParams): string {
  const path = _encode`/${params.regionId}/platform/configuration/security/deployment`
  return `${_getBasePath()}${path}`
}

export interface UpdateSecurityDeploymentUrlParams {
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/configuration/security/deployment/_publish_idp_metadata
 * Operation: `publish-security-deployment-idp-metadata`
 * Publishes IdP metadata to be picked up by SSO-enabled SPs.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 List of public URLs where the IdP metadata are available. See {@link PublishIdPMetadataResponse}
 * @see 404 The realm specified by {realm_id} cannot be found. (code: `security_deployment.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function publishSecurityDeploymentIdpMetadataUrl(
  params: PublishSecurityDeploymentIdpMetadataUrlParams,
): string {
  const path = _encode`/${params.regionId}/platform/configuration/security/deployment/_publish_idp_metadata`
  return `${_getBasePath()}${path}`
}

export interface PublishSecurityDeploymentIdpMetadataUrlParams {
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/security/enrollment-tokens
 * Operation: `get-enrollment-tokens`
 * Retrieves a list of active enrollment tokens.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 A token has been generated that can be used to start new servers with the requested roles. See {@link ListEnrollmentTokenReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getEnrollmentTokensUrl(params: GetEnrollmentTokensUrlParams): string {
  const path = _encode`/${params.regionId}/platform/configuration/security/enrollment-tokens`
  return `${_getBasePath()}${path}`
}

export interface GetEnrollmentTokensUrlParams {
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/configuration/security/enrollment-tokens
 * Operation: `create-enrollment-token`
 * Creates an enrollment token.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link EnrollmentTokenRequest}
 * @see 200 A token has been generated that can be used to start new servers with the requested roles. See {@link RequestEnrollmentTokenReply}
 * @see 400 The token request format was invalid, details in the error (code: 'enrollment_tokens.invalid_token_request'). See {@link BasicFailedReply}
 * @see 403 No signing key is available to generate a token (code: 'enrollment_tokens.signing_key_not_found'). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createEnrollmentTokenUrl(params: CreateEnrollmentTokenUrlParams): string {
  const path = _encode`/${params.regionId}/platform/configuration/security/enrollment-tokens`
  return `${_getBasePath()}${path}`
}

export interface CreateEnrollmentTokenUrlParams {
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/configuration/security/enrollment-tokens/{token}
 * Operation: `delete-enrollment-token`
 * Revokes and deletes the enrollment token.
 * @param {string} params.token The token or token identifier to revoke.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The supplied token has been revoked and can no longer be used to start services on new servers
 * @see 404 Token not found (code: 'enrollment_tokens.invalid_token_id'). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteEnrollmentTokenUrl(params: DeleteEnrollmentTokenUrlParams): string {
  const path = _encode`/${params.regionId}/platform/configuration/security/enrollment-tokens/${params.token}`
  return `${_getBasePath()}${path}`
}

export interface DeleteEnrollmentTokenUrlParams {
  token: string
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/security/realms
 * Operation: `get-security-realm-configurations`
 * Retrieves a list of security realm configurations.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The security realm configurations were successfully returned. See {@link SecurityRealmInfoList}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSecurityRealmConfigurationsUrl(
  params: GetSecurityRealmConfigurationsUrlParams,
): string {
  const path = _encode`/${params.regionId}/platform/configuration/security/realms`
  return `${_getBasePath()}${path}`
}

export interface GetSecurityRealmConfigurationsUrlParams {
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/configuration/security/realms/_reorder
 * Operation: `reorder-security-realms`
 * Reorder security realms.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link SecurityRealmsReorderRequest}
 * @see 200 The reorder request was successful
 * @see 400 * Invalid ids . (code: `security_realm.invalid_ids`)
 * Missing ids . (code: `security_realm.missing_ids`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function reorderSecurityRealmsUrl(params: ReorderSecurityRealmsUrlParams): string {
  const path = _encode`/${params.regionId}/platform/configuration/security/realms/_reorder`
  return `${_getBasePath()}${path}`
}

export interface ReorderSecurityRealmsUrlParams {
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/configuration/security/realms/active-directory
 * Operation: `create-active-directory-configuration`
 * Creates a new Active Directory configuration. .
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link ActiveDirectorySettings}
 * @see 201 The Active Directory configuration was successfully created
 * @see 400 * The realm id is already in use. (code: `security_realm.id_conflict`)
 * The selected id is not valid. (code: `security_realm.invalid_id`)
 * Order must be greater than zero. (code: `security_realm.invalid_order`)
 * Invalid Elasticsearch Security realm type. (code: `security_realm.invalid_type`)
 * The realm order is already in use. (code: `security_realm.order_conflict`)
 * Advanced YAML format is invalid. (code: `security_realm.invalid_yaml`)
 * The url format is invalid. (code: `security_realm.invalid_url`)
 * Invalid Active Directory URL. (code: `security_realm.active_directory.invalid_url`)
 * Invalid certificate bundle URL. (code: `security_realm.invalid_bundle_url`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createActiveDirectoryConfigurationUrl(
  params: CreateActiveDirectoryConfigurationUrlParams,
): string {
  const path = _encode`/${params.regionId}/platform/configuration/security/realms/active-directory`
  return `${_getBasePath()}${path}`
}

export interface CreateActiveDirectoryConfigurationUrlParams {
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/security/realms/active-directory/{realm_id}
 * Operation: `get-active-directory-configuration`
 * Retrieves a single Active Directory security realm configuration. .
 * @param {string} params.realmId The Elasticsearch Security realm identifier.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The Active Directory configuration was successfully retrieved. See {@link ActiveDirectorySettings}
 * @see 404 The realm specified by {realm_id} cannot be found. (code: `security_realm.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getActiveDirectoryConfigurationUrl(
  params: GetActiveDirectoryConfigurationUrlParams,
): string {
  const path = _encode`/${params.regionId}/platform/configuration/security/realms/active-directory/${params.realmId}`
  return `${_getBasePath()}${path}`
}

export interface GetActiveDirectoryConfigurationUrlParams {
  realmId: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/configuration/security/realms/active-directory/{realm_id}
 * Operation: `update-active-directory-configuration`
 * Updates an existing Active Directory configuration. .
 * @param {string} params.realmId The Elasticsearch Security realm identifier.
 * @param {string} params.version When specified, checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link ActiveDirectorySettings}
 * @see 200 The Active Directory configuration was successfully updated
 * @see 400 * The realm id is already in use. (code: `security_realm.id_conflict`)
 * The selected id is not valid. (code: `security_realm.invalid_id`)
 * Order must be greater than zero. (code: `security_realm.invalid_order`)
 * Invalid Elasticsearch Security realm type. (code: `security_realm.invalid_type`)
 * The realm order is already in use. (code: `security_realm.order_conflict`)
 * Advanced YAML format is invalid. (code: `security_realm.invalid_yaml`)
 * The url format is invalid. (code: `security_realm.invalid_url`)
 * Invalid Active Directory URL. (code: `security_realm.active_directory.invalid_url`)
 * Invalid certificate bundle URL. (code: `security_realm.invalid_bundle_url`). See {@link BasicFailedReply}
 * @see 404 The realm specified by {realm_id} cannot be found. (code: `security_realm.not_found`). See {@link BasicFailedReply}
 * @see 409 There is a version conflict. (code: `security_realm.version_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateActiveDirectoryConfigurationUrl(
  params: UpdateActiveDirectoryConfigurationUrlParams,
): string {
  const queryString = _buildQueryString({
    version: params.version,
  })
  const path = _encode`/${params.regionId}/platform/configuration/security/realms/active-directory/${params.realmId}`
  return `${_getBasePath()}${path}${queryString}`
}

export interface UpdateActiveDirectoryConfigurationUrlParams {
  realmId: string
  version?: string | null
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/configuration/security/realms/active-directory/{realm_id}
 * Operation: `delete-active-directory-configuration`
 * Deletes a single Active Directory configuration. .
 * @param {string} params.realmId The Elasticsearch Security realm identifier.
 * @param {string} params.version When specified, checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The Active Directory configuration was successfully deleted
 * @see 404 The realm specified by {realm_id} cannot be found. (code: `security_realm.not_found`). See {@link BasicFailedReply}
 * @see 409 There is a version conflict. (code: `security_realm.version_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteActiveDirectoryConfigurationUrl(
  params: DeleteActiveDirectoryConfigurationUrlParams,
): string {
  const queryString = _buildQueryString({
    version: params.version,
  })
  const path = _encode`/${params.regionId}/platform/configuration/security/realms/active-directory/${params.realmId}`
  return `${_getBasePath()}${path}${queryString}`
}

export interface DeleteActiveDirectoryConfigurationUrlParams {
  realmId: string
  version?: string | null
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/configuration/security/realms/ldap
 * Operation: `create-ldap-configuration`
 * Creates a new LDAP configuration.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link LdapSettings}
 * @see 201 The LDAP configuration was successfully created
 * @see 400 * The realm id is already in use. (code: `security_realm.id_conflict`)
 * The selected id is not valid. (code: `security_realm.invalid_id`)
 * Order must be greater than zero. (code: `security_realm.invalid_order`)
 * Invalid Elasticsearch Security realm type. (code: `security_realm.invalid_type`)
 * The realm order is already in use. (code: `security_realm.order_conflict`)
 * Advanced YAML format is invalid. (code: `security_realm.invalid_yaml`)
 * The url format is invalid. (code: `security_realm.invalid_url`)
 * Invalid LDAP URL. (code: `security_realm.ldap.invalid_url`)
 * Invalid certificate bundle URL. (code: `security_realm.invalid_bundle_url`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createLdapConfigurationUrl(params: CreateLdapConfigurationUrlParams): string {
  const path = _encode`/${params.regionId}/platform/configuration/security/realms/ldap`
  return `${_getBasePath()}${path}`
}

export interface CreateLdapConfigurationUrlParams {
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/security/realms/ldap/{realm_id}
 * Operation: `get-ldap-configuration`
 * Retrieves a single LDAP security realm configuration.
 * @param {string} params.realmId The Elasticsearch Security realm identifier.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The LDAP configuration was successfully retrieved. See {@link LdapSettings}
 * @see 404 The realm specified by {realm_id} cannot be found. (code: `security_realm.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getLdapConfigurationUrl(params: GetLdapConfigurationUrlParams): string {
  const path = _encode`/${params.regionId}/platform/configuration/security/realms/ldap/${params.realmId}`
  return `${_getBasePath()}${path}`
}

export interface GetLdapConfigurationUrlParams {
  realmId: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/configuration/security/realms/ldap/{realm_id}
 * Operation: `update-ldap-configuration`
 * Updates an existing LDAP configuration.
 * @param {string} params.realmId The Elasticsearch Security realm identifier.
 * @param {string} params.version When specified, checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link LdapSettings}
 * @see 200 The LDAP configuration was successfully updated
 * @see 400 * The realm id is already in use. (code: `security_realm.id_conflict`)
 * The selected id is not valid. (code: `security_realm.invalid_id`)
 * Order must be greater than zero. (code: `security_realm.invalid_order`)
 * Invalid Elasticsearch Security realm type. (code: `security_realm.invalid_type`)
 * The realm order is already in use. (code: `security_realm.order_conflict`)
 * Advanced YAML format is invalid. (code: `security_realm.invalid_yaml`)
 * The url format is invalid. (code: `security_realm.invalid_url`)
 * Invalid LDAP URL. (code: `security_realm.ldap.invalid_url`)
 * Invalid certificate bundle URL. (code: `security_realm.invalid_bundle_url`). See {@link BasicFailedReply}
 * @see 404 The realm specified by {realm_id} cannot be found. (code: `security_realm.not_found`). See {@link BasicFailedReply}
 * @see 409 There is a version conflict. (code: `security_realm.version_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateLdapConfigurationUrl(params: UpdateLdapConfigurationUrlParams): string {
  const queryString = _buildQueryString({
    version: params.version,
  })
  const path = _encode`/${params.regionId}/platform/configuration/security/realms/ldap/${params.realmId}`
  return `${_getBasePath()}${path}${queryString}`
}

export interface UpdateLdapConfigurationUrlParams {
  realmId: string
  version?: string | null
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/configuration/security/realms/ldap/{realm_id}
 * Operation: `delete-ldap-configuration`
 * Deletes a single LDAP configuration.
 * @param {string} params.realmId The Elasticsearch Security realm identifier.
 * @param {string} params.version When specified, checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The LDAP configuration was successfully deleted
 * @see 404 The realm specified by {realm_id} cannot be found. (code: `security_realm.not_found`). See {@link BasicFailedReply}
 * @see 409 There is a version conflict. (code: `security_realm.version_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteLdapConfigurationUrl(params: DeleteLdapConfigurationUrlParams): string {
  const queryString = _buildQueryString({
    version: params.version,
  })
  const path = _encode`/${params.regionId}/platform/configuration/security/realms/ldap/${params.realmId}`
  return `${_getBasePath()}${path}${queryString}`
}

export interface DeleteLdapConfigurationUrlParams {
  realmId: string
  version?: string | null
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/configuration/security/realms/saml
 * Operation: `create-saml-configuration`
 * Creates a new SAML configuration.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link SamlSettings}
 * @see 201 The SAML configuration was successfully created
 * @see 400 * The realm id is already in use. (code: `security_realm.id_conflict`)
 * The selected id is not valid. (code: `security_realm.invalid_id`)
 * Order must be greater than zero. (code: `security_realm.invalid_order`)
 * Invalid Elasticsearch Security realm type. (code: `security_realm.invalid_type`)
 * The realm order is already in use. (code: `security_realm.order_conflict`)
 * Advanced YAML format is invalid. (code: `security_realm.invalid_yaml`)
 * The SAML IDP metadata endpoint returned an error response code 200 OK. (code: `security_realm.saml.invalid_idp_metadata_url`)
 * Invalid certificate bundle URL. (code: `security_realm.invalid_bundle_url`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createSamlConfigurationUrl(params: CreateSamlConfigurationUrlParams): string {
  const path = _encode`/${params.regionId}/platform/configuration/security/realms/saml`
  return `${_getBasePath()}${path}`
}

export interface CreateSamlConfigurationUrlParams {
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/security/realms/saml/{realm_id}
 * Operation: `get-saml-configuration`
 * Retrieves a single SAML security realm configuration.
 * @param {string} params.realmId The Elasticsearch Security realm identifier.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The SAML configuration was successfully retrieved. See {@link SamlSettings}
 * @see 404 The realm specified by {realm_id} cannot be found. (code: `security_realm.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSamlConfigurationUrl(params: GetSamlConfigurationUrlParams): string {
  const path = _encode`/${params.regionId}/platform/configuration/security/realms/saml/${params.realmId}`
  return `${_getBasePath()}${path}`
}

export interface GetSamlConfigurationUrlParams {
  realmId: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/configuration/security/realms/saml/{realm_id}
 * Operation: `update-saml-configuration`
 * Updates an existing SAML configuration.
 * @param {string} params.realmId The Elasticsearch Security realm identifier.
 * @param {string} params.version When specified, checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link SamlSettings}
 * @see 200 The SAML configuration was successfully updated
 * @see 400 * The realm id is already in use. (code: `security_realm.id_conflict`)
 * The selected id is not valid. (code: `security_realm.invalid_id`)
 * Order must be greater than zero. (code: `security_realm.invalid_order`)
 * Invalid Elasticsearch Security realm type. (code: `security_realm.invalid_type`)
 * The realm order is already in use. (code: `security_realm.order_conflict`)
 * Advanced YAML format is invalid. (code: `security_realm.invalid_yaml`)
 * The SAML IDP metadata endpoint returned an error response code 200 OK. (code: `security_realm.saml.invalid_idp_metadata_url`)
 * Invalid certificate bundle URL. (code: `security_realm.invalid_bundle_url`). See {@link BasicFailedReply}
 * @see 404 The realm specified by {realm_id} cannot be found. (code: `security_realm.not_found`). See {@link BasicFailedReply}
 * @see 409 There is a version conflict. (code: `security_realm.version_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateSamlConfigurationUrl(params: UpdateSamlConfigurationUrlParams): string {
  const queryString = _buildQueryString({
    version: params.version,
  })
  const path = _encode`/${params.regionId}/platform/configuration/security/realms/saml/${params.realmId}`
  return `${_getBasePath()}${path}${queryString}`
}

export interface UpdateSamlConfigurationUrlParams {
  realmId: string
  version?: string | null
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/configuration/security/realms/saml/{realm_id}
 * Operation: `delete-saml-configuration`
 * Deletes a single SAML configuration.
 * @param {string} params.realmId The Elasticsearch Security realm identifier.
 * @param {string} params.version When specified, checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The SAML configuration was successfully deleted
 * @see 404 The realm specified by {realm_id} cannot be found. (code: `security_realm.not_found`). See {@link BasicFailedReply}
 * @see 409 There is a version conflict. (code: `security_realm.version_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteSamlConfigurationUrl(params: DeleteSamlConfigurationUrlParams): string {
  const queryString = _buildQueryString({
    version: params.version,
  })
  const path = _encode`/${params.regionId}/platform/configuration/security/realms/saml/${params.realmId}`
  return `${_getBasePath()}${path}${queryString}`
}

export interface DeleteSamlConfigurationUrlParams {
  realmId: string
  version?: string | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/security/service-accounts
 * Operation: `list-service-accounts`
 * Retrieves the list of all service accounts.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The list of service accounts was successfully returned. See {@link ServiceAccountList}
 * @return {string} A fully-interpolated and escaped URL
 */
export function listServiceAccountsUrl(params: ListServiceAccountsUrlParams): string {
  const path = _encode`/${params.regionId}/platform/configuration/security/service-accounts`
  return `${_getBasePath()}${path}`
}

export interface ListServiceAccountsUrlParams {
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/configuration/security/service-accounts
 * Operation: `create-service-account`
 * Creates a new service account.
 * @param {boolean} params.acceptUnknownResourceActions When true, accepts resource actions that aren't specified in the domain. Useful to grant resource actions that haven't yet been deployed.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link ServiceAccountCreateRequest}
 * @see 201 The service account was successfully created. See {@link ServiceAccount}
 * @see 400 Service account name is not valid. (code: `service_accounts.invalid_id`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createServiceAccountUrl(params: CreateServiceAccountUrlParams): string {
  const queryString = _buildQueryString({
    accept_unknown_resource_actions:
      params.acceptUnknownResourceActions === undefined
        ? false
        : params.acceptUnknownResourceActions,
  })
  const path = _encode`/${params.regionId}/platform/configuration/security/service-accounts`
  return `${_getBasePath()}${path}${queryString}`
}

export interface CreateServiceAccountUrlParams {
  acceptUnknownResourceActions?: boolean | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/security/service-accounts/{user_id}
 * Operation: `get-service-account`
 * Retrieves details for a single service account.
 * @param {string} params.userId Identifier for the Service Account.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The service account was successfully returned. See {@link ServiceAccount}
 * @see 404 Service account not found. (code: `service_accounts.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getServiceAccountUrl(params: GetServiceAccountUrlParams): string {
  const path = _encode`/${params.regionId}/platform/configuration/security/service-accounts/${params.userId}`
  return `${_getBasePath()}${path}`
}

export interface GetServiceAccountUrlParams {
  userId: string
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/configuration/security/service-accounts/{user_id}
 * Operation: `delete-service-account`
 * Deletes a single service account.
 * @param {string} params.userId Identifier for the Service Account.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The service account was successfully deleted
 * @see 404 Service account not found. (code: `service_accounts.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteServiceAccountUrl(params: DeleteServiceAccountUrlParams): string {
  const path = _encode`/${params.regionId}/platform/configuration/security/service-accounts/${params.userId}`
  return `${_getBasePath()}${path}`
}

export interface DeleteServiceAccountUrlParams {
  userId: string
  regionId: string
}

/**
 * PATCH /api/v1/regions/{region_id}/platform/configuration/security/service-accounts/{user_id}
 * Operation: `patch-service-account`
 * Updates an existing service account using PATCH semantics.
 * @param {string} params.userId Identifier for the Service Account.
 * @param {boolean} params.acceptUnknownResourceActions When true, accepts resource actions that aren't specified in the domain. Useful to grant resource actions that haven't yet been deployed.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link ServiceAccountPatchRequest}
 * @see 200 The service account was successfully updated. See {@link ServiceAccount}
 * @see 404 Service account not found. (code: `service_accounts.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function patchServiceAccountUrl(params: PatchServiceAccountUrlParams): string {
  const queryString = _buildQueryString({
    accept_unknown_resource_actions:
      params.acceptUnknownResourceActions === undefined
        ? false
        : params.acceptUnknownResourceActions,
  })
  const path = _encode`/${params.regionId}/platform/configuration/security/service-accounts/${params.userId}`
  return `${_getBasePath()}${path}${queryString}`
}

export interface PatchServiceAccountUrlParams {
  userId: string
  acceptUnknownResourceActions?: boolean | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/security/service-accounts/{user_id}/keys
 * Operation: `list-service-account-api-keys`
 * Retries the list of API keys for a service account.
 * @param {string} params.userId Identifier for the Service Account.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The list of service account API keys was successfully returned. See {@link ServiceAccountApiKeyList}
 * @see 404 Service account not found. (code: `service_accounts.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function listServiceAccountApiKeysUrl(params: ListServiceAccountApiKeysUrlParams): string {
  const path = _encode`/${params.regionId}/platform/configuration/security/service-accounts/${params.userId}/keys`
  return `${_getBasePath()}${path}`
}

export interface ListServiceAccountApiKeysUrlParams {
  userId: string
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/configuration/security/service-accounts/{user_id}/keys
 * Operation: `create-service-account-api-key`
 * Creates a new API key for a service account.
 * @param {string} params.userId Identifier for the Service Account.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link ServiceAccountApiKeyCreateRequest}
 * @see 201 The service account API key was successfully created. See {@link ServiceAccountApiKey}
 * @see 404 Service account not found. (code: `service_accounts.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createServiceAccountApiKeyUrl(params: CreateServiceAccountApiKeyUrlParams): string {
  const path = _encode`/${params.regionId}/platform/configuration/security/service-accounts/${params.userId}/keys`
  return `${_getBasePath()}${path}`
}

export interface CreateServiceAccountApiKeyUrlParams {
  userId: string
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/configuration/security/service-accounts/{user_id}/keys/{api_key_id}
 * Operation: `delete-service-account-api-key`
 * Deletes an API key for a service account.
 * @param {string} params.userId Identifier for the Service Account.
 * @param {string} params.apiKeyId Identifier for the Service Account API Key.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The service account API key was successfully deleted
 * @see 404 * Service account not found. (code: `service_accounts.not_found`)
 * Service account API key not found. (code: `service_accounts.api_key_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteServiceAccountApiKeyUrl(params: DeleteServiceAccountApiKeyUrlParams): string {
  const path = _encode`/${params.regionId}/platform/configuration/security/service-accounts/${params.userId}/keys/${params.apiKeyId}`
  return `${_getBasePath()}${path}`
}

export interface DeleteServiceAccountApiKeyUrlParams {
  userId: string
  apiKeyId: string
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/security/tls/{service_name}
 * Operation: `get-tls-certificate`
 * Retrieves a certificate in the TLS certificate chain.
 * @param {string} params.serviceName The service certificate chain to read.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The TLS certificate chain, with private key omitted, for the given service and whether it was user specified. See {@link TlsPublicCertChain}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getTlsCertificateUrl(params: GetTlsCertificateUrlParams): string {
  const path = _encode`/${params.regionId}/platform/configuration/security/tls/${params.serviceName}`
  return `${_getBasePath()}${path}`
}

export interface GetTlsCertificateUrlParams {
  serviceName: string
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/configuration/security/tls/{service_name}
 * Operation: `set-tls-certificate`
 * Creates or updates the TLS certificate chain.
 * @param {string} params.serviceName The service certificate chain to read.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link string}
 * @see 202 The TLS update has been accepted for the given service and will take effect throughout the system. See {@link UpdatedTlsChain}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setTlsCertificateUrl(params: SetTlsCertificateUrlParams): string {
  const path = _encode`/${params.regionId}/platform/configuration/security/tls/${params.serviceName}`
  return `${_getBasePath()}${path}`
}

export interface SetTlsCertificateUrlParams {
  serviceName: string
  regionId: string
}
